import React from 'react';
import { Modal } from 'antd';
import { HtmlDeliverableFrame } from './HtmlDeliverableFrame';
import { ApryseDeliverableViewer } from './ApryseDeliverableViewer';
import { DownloadDeliverableFallback } from './DownloadDeliverableFallback';
import { EmlDeliverableViewer } from './EmlDeliverableViewer';
import { getPrimaryDocument, resolveDeliverableViewerArm } from './deliverablePresentation';
import type { DeliverableListItemDto } from './types';

export interface DeliverableViewerModalProps {
  /** The deliverable to view, or `null` when nothing is open. */
  deliverable: DeliverableListItemDto | null;
  /** Whether the modal is open (the page drives this from `activeDeliverable`). */
  open: boolean;
  /** Invoked when the modal is dismissed. */
  onClose: () => void;
}

/**
 * Chooses the viewer for a deliverable's single document (Decision 2 — one document,
 * one format) from the shared `resolveDeliverableViewerArm`, so this switch and the
 * "Open" vs "Get" affordance can never disagree: HTML renders inline in a sandboxed
 * iframe (Mechanism B — Decision 6), PDF/DOCX/XLSX render in the shared Apryse
 * WebViewer, an `.eml` or `.msg` renders in the email viewer, and anything else offers
 * a download. The page owns open/close state; this only renders the body.
 * @param {DeliverableListItemDto} deliverable - The deliverable whose document is viewed.
 * @returns {React.ReactElement} The format-appropriate viewer body.
 */
function renderViewerBody(deliverable: DeliverableListItemDto): React.ReactElement {
  const doc = getPrimaryDocument(deliverable);
  const arm = resolveDeliverableViewerArm(doc);
  if (arm === 'html') {
    return <HtmlDeliverableFrame downloadUrl={doc.downloadUrl} title={deliverable.title} />;
  }
  if (arm === 'apryse') {
    return <ApryseDeliverableViewer downloadUrl={doc.downloadUrl} fileName={doc.fileName} mimeType={doc.mimeType} />;
  }
  if (arm === 'eml' || arm === 'msg') {
    // `fileSizeBytes` is passed explicitly and is a required prop: it drives the pre-fetch
    // half of the size guard, so an oversize message is refused before it is fetched.
    //
    // `kind` comes straight from the arm rather than being re-derived from the file name. That is
    // the point of routing through one resolver: a second name test here could disagree with the
    // first, and the failure mode of a wrong `kind` is a silently EMPTY message.
    return (
      <EmlDeliverableViewer
        kind={arm}
        downloadUrl={doc.downloadUrl}
        fileName={doc.fileName}
        fileSizeBytes={doc.fileSizeBytes}
      />
    );
  }
  return <DownloadDeliverableFallback downloadUrl={doc.downloadUrl} fileName={doc.fileName} />;
}

/**
 * Modal host for the deal deliverable viewer. Branches on the arm the shared resolver
 * gives the single document and renders the matching viewer. `destroyOnClose` guarantees the
 * heavy Apryse instance and the iframe are torn down on close, so swapping between a
 * PDF and an HTML deliverable in one session never leaks a stale viewer.
 * @param {DeliverableViewerModalProps} props - The deliverable plus open/close wiring.
 * @returns {React.ReactElement} The viewer modal.
 */
export function DeliverableViewerModal({
  deliverable,
  open,
  onClose
}: DeliverableViewerModalProps): React.ReactElement {
  return (
    <Modal
      open={open}
      title={deliverable?.title ?? 'Deliverable'}
      footer={null}
      onCancel={onClose}
      destroyOnClose
      centered
      width="90vw"
      bodyStyle={{ height: '80vh', padding: 0, overflow: 'hidden' }}
      aria-label="Deliverable viewer"
    >
      {deliverable ? renderViewerBody(deliverable) : null}
    </Modal>
  );
}
